// Service responsible for building and validating report parameters.
// Used by reportOptionsDialog during report generation.
//
// Each report type is identified by its 'name' attribute, and mapped to a
// specific handler that constructs and validates the required parameters.
//
// TODO: When adding new reports, register their parameter handler here
//       or reuse an existing one if applicable.

import { notify } from '@/global/services/notification/notificationBus'
import { isObject, isSet } from '@/global/utils/utils'
import helperMixin from '@/global/mixins/helperMixin'

export default {
  mixins: [
    helperMixin
  ],

  methods: {
    // validate params for each report
    // method is using handlers for each report
    // return validated params or false if params is invalid
    makeParams () {
      const { name: reportName } = this.reportDetails || {}
      // skip if report name is invalid
      if (!reportName) {
        return
      }

      // report params handlers return params if they are valid
      // otherwise return false
      const mapping = {
        'satellite-tracking/report.day_by_day_report': this.makeVehiclesDayByDayReportParams,
        'satellite-tracking/report.summary_report': this.makeVehiclesSummaryReportParams,
        'satellite-tracking/report.trips_report': this.makeVehiclesTripsReportParams,
        'satellite-tracking/report.stops_report': this.makeVehiclesStopsReportParams,
        'satellite-tracking/report.overspeed_report': this.makeVehiclesAbsoluteOverspeedReportParams,
        'satellite-tracking/report.road_overspeed': this.makeVehiclesRoadOverspeedReportParams,
        'satellite-tracking/report.low_voltage_level': this.makeVehiclesLowVoltageLevelReportParams,
        'satellite-tracking/report.geofence_report': this.makeVehiclesGeofenceReportParams,
        'satellite-tracking/report.idle_vehicle_operation': this.makeVehiclesIdleVehicleOperationReportParams,
        'satellite-tracking/report.machine_operation': this.makeVehiclesMachineOperationReportParams,
        'satellite-tracking/report.temperature_report': this.makeVehiclesTemperatureReportParams,
        'satellite-tracking/report.expenses_report': this.makeVehiclesExpensesReportParams,
        'satellite-tracking/report.fuel_refuel_report': this.makeVehiclesFuelRefuelReportParams,
        'satellite-tracking/report.monthly_mileage_hours_report': this.makeVehiclesMonthlyMileageHoursReportParams,
        'satellite-tracking/report.engine_on_off_report': this.makeVehiclesEngineOnOffReportParams,
        'satellite-tracking/report.truck_operation_report': this.makeVehiclesTruckOperationReportParams,
        'satellite-tracking/report.through_location_report': this.makeVehiclesPassingThroughLocationReportParams,
        'satellite-tracking/report.vehicle_sensors': this.makeVehiclesSensorsReportParams,
        'satellite-tracking/report.business_private_drive': this.makeVehiclesPrivateDriverReportParams,
        'satellite-tracking/report.availability_status_report': this.makeVehiclesAvailabilityReportParams,
        'satellite-tracking/report.activity_history_report': this.makeVehiclesActivityHistoryReportParams,
        'satellite-tracking/report.violations_report': this.makeVehiclesIncidentViolationsReportParams,
        'satellite-tracking/report.damage_report': this.makeVehiclesIncidentDamagesReportParams,
        'satellite-tracking/report.drivers_by_day_report': this.makeDriversDayByDayReportParams,
        'satellite-tracking/report.drivers_summary_report': this.makeDriversSummaryReportParams,
        'satellite-tracking/report.drivers_trips_report': this.makeDriversTripsReportParams,
        'satellite-tracking/report.drivers_stops_report': this.makeDriversStopsReportParams,
        'satellite-tracking/report.drivers_violations_report': this.makeDriversViolationsReportParams,
        'satellite-tracking/report.drivers_damage_report': this.makeDriversDamagesReportParams
      }

      const handler = mapping?.[reportName]
      if (!handler) {
        this.pushNotification(this.$t('base/reports.undefined_param_builder').replace(':reportName', '"' + this.$t(reportName) + '"'))
        return null
      }
      return handler()
    },

    makeVehiclesDayByDayReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        // backend strictly checks === 1, so this must stay an int, not a bool
        isSelectedVehicleTypeMachine: this.isMachine ? 1 : 0
      }
    },

    makeVehiclesSummaryReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeVehiclesTripsReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeVehiclesStopsReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || !this.stopsDurationRange?.length) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        stopsDurationRange: this.stopsDurationRange
      }
    },

    makeVehiclesAbsoluteOverspeedReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.idleVehicleMinTime === undefined || this.maxSpeedValue === undefined) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        idleVehicleMinTime: this.idleVehicleMinTime,
        maxSpeedValue: this.maxSpeedValue
      }
    },

    makeVehiclesRoadOverspeedReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.speedLimitTolerance === undefined) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        speedLimitTolerance: this.speedLimitTolerance
      }
    },

    makeVehiclesLowVoltageLevelReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.lowVoltageValue === undefined) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        lowVoltageValue: this.lowVoltageValue
      }
    },

    makeVehiclesGeofenceReportParams () {
      const vehicleIds = this.vehicleIds
      const locationIds = this.locationIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.fromMinutes === undefined || this.toMinutes === undefined || !locationIds?.length) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        fromMinutes: this.fromMinutes,
        toMinutes: this.toMinutes,
        locationIds
      }
    },

    makeVehiclesIdleVehicleOperationReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.idleVehicleMinTime === undefined) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        idleVehicleMinTime: this.idleVehicleMinTime
      }
    },

    makeVehiclesMachineOperationReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesTemperatureReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds,
        ignoreMainLocations: this.ignoreMainLocations,
        ignoreUnloadedVehicle: this.ignoreUnloadedVehicle,
        temperatureRanges: this.temperatureRanges
      }
    },

    makeVehiclesExpensesReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeVehiclesFuelRefuelReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesMonthlyMileageHoursReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesEngineOnOffReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesTruckOperationReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesPassingThroughLocationReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || this.fromMinutes === undefined || this.toMinutes === undefined || !isSet(this.latitude) || !isSet(this.longitude)) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        fromMinutes: this.fromMinutes,
        toMinutes: this.toMinutes,
        radius: this.radius,
        latitude: this.latitude,
        longitude: this.longitude
      }
    },

    makeVehiclesSensorsReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      if (!Object.keys(this.getVehicleAssignedSensorsBetweenDates ?? {}).length) {
        this.pushNotification(this.$t('satellite-tracking/report.vehicle_has_no_sensors'))
        return false
      }

      if (!isSet(this.selectedSensor) || !isObject(this.selectedSensor) || !isSet(this.selectedSensor.id)) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        selectedSensor: this.selectedSensor
      }
    },

    makeVehiclesPrivateDriverReportParams () {
      // use the existing handler because they currently have the same params
      return this.makeVehiclesDayByDayReportParams()
    },

    makeVehiclesAvailabilityReportParams () {
      const vehicleIds = this.vehicleIds
      if (!this.dateFrom || !this.dateTo || !vehicleIds?.length || !this.availabilityStatus) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        vehicleIds,
        availabilityStatus: this.availabilityStatus
      }
    },

    makeDriversDayByDayReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      if (!this.dateFrom || !this.dateTo || !personIds?.length) {
        this.pushNotification(this.$t('satellite-tracking/report.you_should_choose_all_parameters'))
        return false
      }

      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        personId: personIds
      }
    },

    makeDriversSummaryReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      // use the existing handler because they currently have the same params
      const baseParams = this.makeDriversDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        personId: personIds
      }
    },

    makeVehiclesActivityHistoryReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeVehiclesIncidentViolationsReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeVehiclesIncidentDamagesReportParams () {
      // use the existing handler because they currently have the same params
      const baseParams = this.makeVehiclesDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        vehicleIds: this.vehicleIds
      }
    },

    makeDriversTripsReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      // use the existing handler because they currently have the same params
      const baseParams = this.makeDriversDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        personId: personIds
      }
    },

    makeDriversStopsReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      // use the existing handler because they currently have the same params
      const baseParams = this.makeDriversDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        personId: personIds,
        stopsDurationRange: this.stopsDurationRange
      }
    },

    makeDriversViolationsReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      // use the existing handler because they currently have the same params
      const baseParams = this.makeDriversDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        personId: personIds
      }
    },

    makeDriversDamagesReportParams () {
      const personIds = this.selectedPersons.map(person => person?.id).filter(person => person)
      // use the existing handler because they currently have the same params
      const baseParams = this.makeDriversDayByDayReportParams()
      if (!baseParams) {
        return false
      }
      return {
        ...baseParams,
        personId: personIds
      }
    },

    pushNotification (message) {
      notify(message)
    }
  }
}
